import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Store, CheckCircle2, Send } from "lucide-react";
import { Reveal, Eyebrow } from "./Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CHANNELS = [
  "Pharmacies",
  "Baby-Care Stores",
  "Supermarkets",
  "Online Retailers",
  "International Distributors",
];

const TYPES = ["Pharmacy", "Baby-care store", "Supermarket", "Online retailer", "International distributor", "Other"];

const inputCls =
  "w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-900 outline-none transition-all duration-300 placeholder:text-slate-400 focus:border-binky-pink focus:ring-4 focus:ring-binky-pink/10";

export const Retailers = () => {
  const [form, setForm] = useState({ name: "", business_name: "", email: "", business_type: "", country: "", message: "" });
  const [status, setStatus] = useState("idle");

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (status === "sending") return;
    setStatus("sending");
    try {
      await axios.post(`${API}/retail-enquiry`, form);
      setStatus("done");
      toast.success("Thank you — your retail enquiry has been received.");
    } catch (err) {
      setStatus("idle");
      toast.error(err?.response?.data?.detail || "Something went wrong — please try again.");
    }
  };

  return (
    <section id="retailers" data-testid="retailers-section" className="relative overflow-hidden py-24 sm:py-32">
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <div className="grid grid-cols-1 items-center gap-14 lg:grid-cols-2">
          <Reveal>
            <Eyebrow>Retail Display</Eyebrow>
            <h2 className="mt-4 font-display text-3xl font-extrabold tracking-tight text-slate-900 sm:text-4xl">
              Mi Binky <span className="text-binky-blue">for Retailers.</span>
            </h2>
            <p className="mt-4 text-base leading-relaxed text-slate-600 md:text-lg">
              Mi Binky is preparing for retail with a shelf-ready blister pack and a compact
              countertop display — designed to sit naturally in the baby-care aisle.
            </p>
            <div className="mt-6 flex flex-wrap gap-2.5" data-testid="retailer-channels">
              {CHANNELS.map((c) => (
                <span
                  key={c}
                  className="inline-flex items-center gap-2 rounded-full border border-binky-blue/15 bg-white px-4 py-1.5 text-xs font-bold text-binky-blue"
                >
                  <Store className="h-3.5 w-3.5 text-binky-pink" />
                  {c}
                </span>
              ))}
            </div>
            <div className="group relative mt-8 overflow-hidden rounded-[32px] border border-slate-100 shadow-2xl shadow-binky-blue/15" data-testid="retail-display-block">
              <img
                src="/assets/retail-display.jpg"
                alt="Mi Binky countertop retail display in a pharmacy baby-care aisle"
                loading="lazy"
                className="w-full object-cover transition-transform duration-700 group-hover:scale-[1.03]"
                data-testid="retail-display-image"
              />
              <span className="absolute left-5 top-5 rounded-full bg-white/90 px-4 py-2 text-xs font-bold uppercase tracking-[0.14em] text-binky-blue shadow backdrop-blur">
                Pharmacy &amp; supermarket ready
              </span>
            </div>
          </Reveal>

          <Reveal delay={0.15}>
            <div className="rounded-[32px] border border-slate-100 bg-white p-8 shadow-[0_24px_60px_-24px_rgba(27,79,160,0.3)] sm:p-10" data-testid="retail-form-card">
              {status === "done" ? (
                <div className="flex flex-col items-center py-10 text-center" data-testid="retail-success-message">
                  <span className="flex h-16 w-16 items-center justify-center rounded-full bg-binky-green/15 text-binky-green">
                    <CheckCircle2 className="h-8 w-8" />
                  </span>
                  <h3 className="mt-6 font-display text-2xl font-extrabold text-slate-900">Enquiry received</h3>
                  <p className="mt-2 max-w-sm text-sm leading-relaxed text-slate-600">
                    Thank you for your interest in stocking Mi Binky. Our team will be in touch as
                    retail preparations progress.
                  </p>
                </div>
              ) : (
                <>
                  <h3 className="font-display text-2xl font-extrabold text-slate-900">
                    Retail &amp; Distribution Enquiries
                  </h3>
                  <p className="mt-2 text-sm text-slate-600">
                    Tell us about your business and we&apos;ll be in touch ahead of launch — or
                    email us directly at{" "}
                    <a href="mailto:info@binky.life" data-testid="retail-email-link" className="font-bold text-binky-blue underline decoration-binky-pink/40 underline-offset-2 hover:text-binky-pink">
                      info@binky.life
                    </a>
                    .
                  </p>
                  <form onSubmit={submit} className="mt-7 space-y-4" data-testid="retail-enquiry-form">
                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                      <input required data-testid="retail-name-input" value={form.name} onChange={set("name")} placeholder="Your name" className={inputCls} />
                      <input required data-testid="retail-business-input" value={form.business_name} onChange={set("business_name")} placeholder="Business name" className={inputCls} />
                    </div>
                    <input required type="email" data-testid="retail-email-input" value={form.email} onChange={set("email")} placeholder="Business email" className={inputCls} />
                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                      <select required data-testid="retail-type-select" value={form.business_type} onChange={set("business_type")} className={inputCls}>
                        <option value="" disabled>Business type</option>
                        {TYPES.map((t) => (
                          <option key={t} value={t}>{t}</option>
                        ))}
                      </select>
                      <input data-testid="retail-country-input" value={form.country} onChange={set("country")} placeholder="Country" className={inputCls} />
                    </div>
                    <textarea data-testid="retail-message-input" value={form.message} onChange={set("message")} placeholder="Tell us about your stores, channels or distribution network (optional)" rows={4} className={`${inputCls} resize-none`} />
                    <button
                      type="submit"
                      data-testid="retail-submit-button"
                      disabled={status === "sending"}
                      className="group inline-flex w-full items-center justify-center gap-2 rounded-full bg-binky-blue px-7 py-4 text-sm font-bold text-white shadow-lg shadow-binky-blue/30 transition-transform duration-300 hover:scale-[1.02] active:scale-95 disabled:opacity-60"
                    >
                      <Send className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
                      {status === "sending" ? "Sending…" : "Send Enquiry"}
                    </button>
                  </form>
                </>
              )}
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
};
